import { Component, OnInit } from '@angular/core';
import { AlertController, LoadingController, ToastController } from '@ionic/angular';
import { Router } from '@angular/router';
import { NaploService } from '../naplo.service';
import { DataService } from '../../data.service';

// NAPLO – eÁFA (NAV eVAT M2M). Áfa-analitika a könyvelt tételekből + gépi ÁFA-bevallás folyamata:
// feltöltés → véglegesítés (itt adja a NAV a feldolgozási azonosítót) → állapot → tervezet →
// beküldés. A NAV technikai felhasználó ugyanaz, mint az Online Számláé (annak környezetével).
@Component({
  selector: 'app-naplo-eafa',
  templateUrl: './eafa.component.html',
  styleUrls: ['../naplo-shared.scss', './eafa.component.scss'],
})
export class EafaComponent implements OnInit {
  /** Az ÁFA-kulcs százalékban: a tárolt érték százalék (27); régebbi adatnál tört (0,27) is lehet. */
  kulcsSzazalek(v: any): string {
    if (v == null || v === '') return '—';
    const n = Number(v);
    return `${Math.round((n > 0 && n < 1 ? n * 100 : n) * 100) / 100}%`;
  }

  ev = new Date().getFullYear();
  idoszak = '';                 // YYYY-MM vagy YYYY-Qn
  honapok = Array.from({ length: 12 }, (_, i) => i + 1);
  negyedevek = [1, 2, 3, 4];
  bontas: 'ho' | 'negyedev' = 'ho';
  hoSel = new Date().getMonth() + 1;
  qSel = 1;

  allapotok = ['tervezet', 'feltoltve', 'feldolgozva', 'veglegesitve', 'bekuldve'];
  bevallas: any = null;         // { letezik, allapot, analitika, tervezet, ... }
  bevallasok: any[] = [];
  forras: 'konyveles' | 'osa' = 'konyveles';   // az analitika forrása: könyvelés v. Online Számla

  /*  NAV-KAPCSOLAT: az eÁFA az ügyfélnél beállított NAV technikai felhasználóval megy, és abba a
   *  környezetbe (teszt/éles), amit ott a „sandbox" jelölés mond. Itt csak az állapota látszik, a
   *  beállítás helye a Szervíz → NAV technikai felhasználó képernyő. */
  kapcsolat: any = null;
  kapFut: string | null = null;
  /*  ELLENŐRZŐ SOROK: a bevallás összesítő sorai (36/76/83/84–85) és a kontrollok – főkönyv,
   *  Online Számla, besorolatlan tételek. Ezekkel nézi át a könyvelő beadás előtt az adatokat. */
  ellenorzes: any = null;
  ellFut = false;
  /*  AZ UTOLSÓ NAV-LÉPÉS EREDMÉNYE. Korábban csak egy toast villant fel, a NAV nyers XML-je pedig
   *  a státuszsorba került – abból a könyvelő nem tudta, mit kell javítania. Itt tartjuk a
   *  feldolgozás állapotát és a NAV tételes kifogásait, amíg be nem zárja. */
  utolsoLepes: any = null;

  constructor(private naplo: NaploService, private toast: ToastController, private alert: AlertController,
              private loadingCtrl: LoadingController, private router: Router, private data: DataService) {}

  get aktiv() { return this.naplo.aktiv; }

  async ngOnInit() {
    await this.naplo.init();
    this.frissitIdoszak();
    if (this.aktiv.ugyfelId) { await this.betoltLista(); await this.betolt(); await this.kapcsolatBetolt(); }
  }

  frissitIdoszak() {
    this.idoszak = this.bontas === 'ho' ? `${this.ev}-${String(this.hoSel).padStart(2, '0')}` : `${this.ev}-Q${this.qSel}`;
  }

  async kapcsolatBetolt() {
    if (!this.aktiv.ugyfelId) return;
    try { this.kapcsolat = await this.naploEafaGet('/kapcsolat/' + this.aktiv.ugyfelId); }
    catch { this.kapcsolat = null; }
  }
  /** A hitelesítő megadása egy helyen történik: Szervíz → NAV technikai felhasználó. */
  technikaiFelhasznalora() { this.router.navigate(['/naplo/torzsek'], { queryParams: { nezet: 'osa' } }); }

  /*  NYOMTATVÁNY-MUNKAPÉLDÁNY: a bevallási sorok kinyomtatható, nyomtatvány-szerű képe – az
   *  átszokáshoz, ellenőrzéshez. Külön lapon nyílik (a kiszolgáló HTML-t ad, a böngésző nyomtatja). */
  munkapeldany() {
    if (!this.aktiv.ugyfelId) return;
    this.frissitIdoszak();
    this.data.megnyitFajl(`/api/naplo-eafa/nyomtatvany/${this.aktiv.ugyfelId}?idoszak=${this.idoszak}`, 'ÁFA munkapéldány');
  }

  /** Ártalmatlan lekérdezés: elfogadja-e a NAV a hitelesítőt abban a környezetben. */
  async kapcsolatProba() {
    this.kapFut = 'proba';
    try {
      const r: any = await this.naploEafaPost('/kapcsolat/' + this.aktiv.ugyfelId + '/proba', {});
      await this.kapcsolatBetolt();
      if (r.ok) this.ok(`A NAV ${r.kornyezet === 'teszt' ? 'tesztkörnyezete' : 'éles rendszere'} elfogadta a hitelesítőt.`);
      else {
        this.hiba(`${r.hiba || r.errorCode || ''} ${r.uzenet || ''}`.trim() || 'A NAV nem fogadta el a hitelesítőt.');
        //  A puszta hibakód nem mond semmit: a teendőt külön, hosszabban is kiírjuk.
        if (r.magyarazat) this.figyelmeztet(r.magyarazat);
      }
    } catch (e: any) { this.hiba(e?.error?.error || 'A próba nem sikerült.'); }
    finally { this.kapFut = null; }
  }

  /** Ellenőrző sorok az időszakra (a NAV-ot nem hívja, a saját adatainkat méri össze). */
  async ellenorzesFrissit() {
    if (!this.aktiv.ugyfelId) { this.ellenorzes = null; return; }
    this.frissitIdoszak();
    this.ellFut = true;
    try { this.ellenorzes = await this.naploEafaGet(`/ellenorzes/${this.aktiv.ugyfelId}?ev=${this.ev}&idoszak=${this.idoszak}`); }
    catch (e: any) { this.ellenorzes = null; }
    finally { this.ellFut = false; }
  }

  /** Az A60-jelzés sora (ha az időszakban közösségi ügylet van). */
  get a60Sor(): any { return (this.ellenorzes?.sorok || []).find((s: any) => s.a60) || null; }
  a60Nyit() { this.router.navigate(['/naplo/bevallasok']); }

  /** Pótolható (hiányzó kulcsú / bevallási sor nélküli) áfás tételsorok az ellenőrzés szerint. */
  get potolhato(): number { return (this.ellenorzes?.sorok || []).reduce((n: number, s: any) => n + (Number(s.potolhato) || 0), 0); }
  potlasFut = false;
  /** Utólagos pótlás: a hiányzó ÁFA-adatok beírása az egész évre, utána újraellenőrzés. */
  async potlas() {
    if (!this.aktiv.ugyfelId) return;
    this.potlasFut = true;
    try {
      const r: any = await this.naploEafaPost(`/potlas/${this.aktiv.ugyfelId}?ev=${this.ev}`, {});
      this.ok(`Pótolva: ${r.kulcs} áfakulcs, ${r.sor} bevallási sor${r.maradt ? ` – ${r.maradt} sort kézzel kell besorolni` : ''}.`);
      await this.ellenorzesFrissit();
    } catch (e: any) { this.hiba(e?.error?.error || 'A pótlás nem sikerült.'); }
    finally { this.potlasFut = false; }
  }

  async betoltLista() {
    try { this.bevallasok = await this.naploEafaGet('/bevallasok/' + this.aktiv.ugyfelId); } catch { this.bevallasok = []; }
  }
  async betolt() {
    this.frissitIdoszak();
    try { this.bevallas = await this.naploEafaGet('/bevallas/' + this.aktiv.ugyfelId + '?idoszak=' + this.idoszak); }
    catch { this.bevallas = null; }
    await this.ellenorzesFrissit();
  }

  // az eÁFA külön mount-prefix alatt van (/api/naplo-eafa), a NaploService base /api/naplo –
  // ezért az abszolút utat a http-n át hívjuk a service segédjével
  private naploEafaGet(path: string) { return this.naplo.getAbs('/api/naplo-eafa' + path); }
  private naploEafaPost(path: string, body: any = {}) { return this.naplo.postAbs('/api/naplo-eafa' + path, body); }

  async analitika() {
    if (!this.aktiv.ugyfelId) return;
    this.frissitIdoszak();
    const forrasCimke = this.forras === 'osa' ? 'Online Számla adataiból' : 'könyvelésből';
    const l = await this.loadingCtrl.create({ message: 'Áfa-analitika összeállítása (' + forrasCimke + ')…' });
    await l.present();
    try {
      const r: any = await this.naploEafaPost('/analitika/' + this.aktiv.ugyfelId + '?ev=' + this.ev + '&idoszak=' + this.idoszak + '&forras=' + this.forras, {});
      await this.betolt(); await this.betoltLista();
      this.ok('Analitika összeállítva (' + forrasCimke + '). Fizetendő−levonható = ' + (r?.analitika?.elszamolando_afa ?? '?') + ' Ft.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Az analitika összeállítása nem sikerült.'); }
    finally { await l.dismiss(); }
  }

  async lepes(muvelet: string, cimke: string) {
    if (!this.aktiv.ugyfelId) return;
    const conf = await this.alert.create({
      header: cimke, message: `Biztosan: ${cimke} – ${this.idoszak} időszak? (NAV M2M művelet)`,
      buttons: ['Mégse', { text: 'Igen', handler: () => this.lepesFut(muvelet, cimke) }],
    });
    await conf.present();
  }
  private async lepesFut(muvelet: string, cimke: string) {
    const l = await this.loadingCtrl.create({ message: cimke + '…' });
    await l.present();
    try {
      const r: any = await this.naploEafaPost('/' + muvelet + '/' + this.aktiv.ugyfelId + '?idoszak=' + this.idoszak, {});
      await this.betolt(); await this.betoltLista();
      //  a NAV válaszát megtartjuk a képernyőn (állapot + tételes kifogások)
      const megszakadt = r?.feldolgozas?.kod && r.feldolgozas.kod !== 'FINISHED' && r.feldolgozas.kod !== 'DONE';
      this.utolsoLepes = { ...r, cimke, baj: !r?.ok || !!(r?.hibak || []).some((h: any) => h.szint === 'ERROR') || megszakadt };
      if (this.utolsoLepes.baj) this.hiba(r?.uzenet || (cimke + ': a NAV kifogásolt valamit.'));
      else this.ok(cimke + ' kész.');
      if (r?.figyelmeztetes) this.figyelmeztet(r.figyelmeztetes);
    } catch (e: any) {
      this.utolsoLepes = { cimke, baj: true, uzenet: e?.error?.error || (cimke + ' nem sikerült.'),
        magyarazat: e?.error?.magyarazat, hibak: e?.error?.hibak || [] };
      this.hiba(this.utolsoLepes.uzenet);
    }
    finally { await l.dismiss(); }
  }

  /** A NAV-nak felküldött analitika-XML megtekintése (ugyanaz, ami a feltöltésben megy). */
  analitikaXml() {
    if (!this.aktiv.ugyfelId) return;
    this.data.megnyitFajl(`/api/naplo-eafa/analitika-xml/${this.aktiv.ugyfelId}?idoszak=${this.idoszak}`, 'eÁFA analitika');
  }
  /** A NAV-tól lehozott tervezet XML-je új lapon. */
  tervezetXml() {
    const xml = this.bevallas?.tervezet?.xml;
    if (!xml) return;
    const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }));
    window.open(url, '_blank');
  }

  /* ══════════ BEKÜLDÖTT BEVALLÁS MÓDOSÍTÁSA ══════════
   *  Beküldött bevallás nem törölhető: önellenőrzés, helyesbítés, vagy – a NAV felhívására – javítás.
   *  Mindegyik a TELJES analitikát újraküldi, az eredeti bevallásra hivatkozva; önellenőrzésnél a
   *  04-es lap is megy (a kötelezettségváltozást a program számolja, a pótlék összegét a könyvelő
   *  adja meg – a NAV pótlékkalkulátora szerint). Utána a szokott lépések: feltöltés → véglegesítés → beküldés. */
  modositasAblak: any = null;
  readonly potlekOkok = [
    { v: '', c: '— általános szabály szerint —' },
    { v: '01', c: '01 – eÁFA-n beadott bevallás önellenőrzése 15 napon belül (pótlékmentes, Art. 211. § (3) c))' },
    { v: '02', c: '02 – az adót már megfizette: legfeljebb 5 000 Ft (Art. 212. § (2))' },
    { v: '03', c: '03 – későbbi bevallásban bevallotta és megfizette (Art. 212. § (3))' },
    { v: '04', c: '04 – termékimport ÁFA-ja az előző időszakban (Art. 212. § (4))' },
    { v: '05', c: '05 – önellenőrzési lánc, átvihető követelés (Art. 212. § (5))' },
    { v: '06', c: '06 – határidő előtt benyújtott bevallás (Art. 2. sz. mell. I/A/8.)' },
    { v: '07', c: '07 – tárgyi eszköz utólagos kiigazítása febr. 15-ig (I/B/1.2)' },
    { v: '08', c: '08 – Áfa tv. 226. § (2) viszonteladó' },
    { v: '09', c: '09 – több jogcím együtt' },
  ];
  modositasNyit(fajta: 'SELF_CHECK' | 'CORRECTION' | 'JAVITAS') {
    this.modositasAblak = { fajta, vonalkod: '', potlek: null, ok: '', potlekmentes: false, eredeti_kulonbozet_ezer: null, kellEredeti: false };
  }
  get modositasCim(): string {
    return { SELF_CHECK: 'Önellenőrzés', CORRECTION: 'Helyesbítés', JAVITAS: 'Javítás a NAV felhívására' }[this.modositasAblak?.fajta as string] || '';
  }
  async modositasIndit() {
    const m = this.modositasAblak;
    if (!m) return;
    try {
      const r: any = await this.naploEafaPost('/modositas/' + this.aktiv.ugyfelId, {
        idoszak: this.idoszak, fajta: m.fajta, vonalkod: m.vonalkod, potlek: m.potlek, ok: m.ok || null,
        potlekmentes: m.ok === '01' || m.potlekmentes, eredeti_kulonbozet_ezer: m.eredeti_kulonbozet_ezer });
      this.modositasAblak = null;
      await this.betolt(); await this.betoltLista();
      this.ok(`${this.modositasCim || 'Módosítás'} előkészítve (hivatkozás: ${r.hivatkozas_id}, ${r.hivatkozas_index}. módosítás). Most: 1. Feltöltés → 2. Véglegesítés → 5. Beküldés.`);
    } catch (e: any) {
      if (e?.error?.kell_eredeti) { m.kellEredeti = true; }
      this.hiba(e?.error?.error || 'Nem sikerült előkészíteni a módosítást.');
    }
  }
  async modositasElvet() {
    try { await this.naploEafaPost('/modositas-elvet/' + this.aktiv.ugyfelId, { idoszak: this.idoszak }); await this.betolt(); this.ok('A módosítás elvetve.'); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült.'); }
  }
  jellegNev(j: string, jav?: boolean): string {
    const n = ({ BASE: 'alapbevallás', SELF_CHECK: 'önellenőrzés', CORRECTION: 'helyesbítés' } as any)[j] || j;
    return jav ? `${n} (javítás NAV-felhívásra)` : n;
  }

  allapotIdx(a: string) { return this.allapotok.indexOf(a); }
  vanAllapot(a: string) { return this.bevallas?.allapot && this.allapotIdx(this.bevallas.allapot) >= this.allapotIdx(a); }

  private async figyelmeztet(m: string) { (await this.toast.create({ message: m, duration: 12000, color: 'warning', position: 'top', buttons: [{ text: 'Rendben', role: 'cancel' }] })).present(); }
  private async ok(m: string) { (await this.toast.create({ message: m, duration: 2200, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 3400, color: 'danger' })).present(); }
}
